const axios = require('axios');
const ChainedBackend = require('i18next-chained-backend').default;
const LocalStorageBackend = require('i18next-localstorage-backend').default;
const { defaultLanguageCode, supportedLanguagesCodes } = require('./utils/i18nConstants.ts');

const isBrowser = typeof window !== 'undefined';

function getMissingKeys(obj1, obj2) {
  let missingKeysObj = {};
  for (let key in obj1) {
    if (!(key in obj2)) {
      missingKeysObj[key] = obj1[key];
    }
  }

  return missingKeysObj;
}

class CustomBackend {
  constructor(services, options = {}, allOptions = {}) {
    if (!isBrowser) {
      throw new Error('CustomBackend can only be used in the browser');
    }

    const getConfig = require('next/config').default;
    const { publicRuntimeConfig } = getConfig();
    this.basePath = publicRuntimeConfig.basePath;
    this.services = services;
    this.options = options;
    this.allOptions = allOptions;
    this.init(services, options, allOptions);
  }

  init(services, options = {}, allOptions = {}) {
    this.services = services;
    this.options = options;
    this.allOptions = allOptions;
  }

  getLocalTranslation(language, namespace) {
    return this.services.resourceStore.data[language]?.[namespace];
  }

  getBaseTranslation(namespace) {
    return this.getLocalTranslation(this.allOptions.defaultLocale, namespace);
  }

  async translateJSON(language, baseTranslation) {
    const { data: translation } = await axios.post(
      `${this.basePath}/api/translateJSON`,
      {
        locale: language,
        baseTranslation,
      },
      {
        headers: {
          'Public-Authorization': process.env.NEXT_PUBLIC_PUBLIC_AUTH_TOKEN,
        },
      }
    );

    return translation;
  }

  async read(language, namespace, callback) {
    try {
      const baseTranslation = this.getBaseTranslation(namespace);

      if (!baseTranslation) {
        throw new Error('Base translation not found:', {
          defaultLocale: this.allOptions.defaultLocale,
          namespace,
        });
      }

      // if we have a complete local translation, return it
      const localTranslation = this.getLocalTranslation(language, namespace);
      const missingKeys = getMissingKeys(baseTranslation, localTranslation);

      if (!!localTranslation && Object.keys(missingKeys).length === 0) {
        callback(null, localTranslation);
        return;
      }

      // translate missing keys
      const missingKeysTranslation = await this.translateJSON(language, missingKeys);

      // combine and return the translations
      const newTranslation = { ...(localTranslation || {}), ...missingKeysTranslation };
      callback(null, newTranslation);
    } catch (error) {
      console.error('Error reading translation file:', error);
      callback(error, null);
    }
  }
}

CustomBackend.type = 'backend';

/** @type {import('next-i18next').UserConfig} */
module.exports = {
  debug: false,
  i18n: {
    defaultLocale: defaultLanguageCode,
    locales: supportedLanguagesCodes,
  },
  backend: {
    backendOptions: [
      {
        expirationTime: 60 * 60 * 1000, // 1 hour cache time
      },
    ],
    backends: isBrowser ? [LocalStorageBackend, CustomBackend] : [],
    handleEmptyResourcesAsFailed: false,
  },
  fallbackLng: false,
  defaultNS: [],
  keySeparator: false,
  nsSeparator: ':',
  use: isBrowser ? [ChainedBackend] : [],
  serializeConfig: false,
  react: {
    useSuspense: false,
    bindI18n: 'languageChanged loaded',
  },
  partialBundledLanguages: true,
};
